import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { EpisodeStates } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'

import { getAuthUserId } from '../../../../data/auth/authSelectors'
import { RTKQueryHook, RTKQueryResultType } from '../../../../data/baseApi'
import { useSelectedClientData } from '../../../clients/clientDetails/data/hooks/useSelectedClientData'
import { useEpisodesForSelectedClient } from '../../../episodes/data/hooks/useEpisodesForSelectedClient'
import { useGetAIDraftQuery } from '../messagesApi'

export const useSelectedClientAIDraftData: RTKQueryHook<typeof useGetAIDraftQuery> = (args, opts) => {
  const { shouldShowMessengerAIDrafts, shouldShowPostSessionAIDrafts } = useFlags()
  const userId = useSelector(getAuthUserId)
  const { data: episodes } = useEpisodesForSelectedClient()
  const { data: client } = useSelectedClientData()

  const activeEpisode = useMemo(
    () => episodes?.find((episode) => episode.state === EpisodeStates.IN_PROGRESS && episode.patient_id === client?.id),
    [episodes, client?.id],
  )
  // Coaching doesn't generate an intake summary, so shouldShowMessengerAIDrafts (which gates the
  // session-1 fetch via runInitialDrafts) is off for coaches. Their first session summary still appears
  // while session_number is 1, so post-session drafts must be fetchable from session 1 too — otherwise
  // a coach's first session summary is never fetched and can't be inserted (LCDELIVERY-7477).
  const runPostSessionDrafts = client?.session_number && client?.session_number >= 1 && shouldShowPostSessionAIDrafts
  const runInitialDrafts = client?.session_number && client?.session_number === 1 && shouldShowMessengerAIDrafts
  return useGetAIDraftQuery(
    activeEpisode?.id && client
      ? {
          providerId: userId,
          episodeId: activeEpisode.id,
          sessionNumber: client.session_number,
          clientId: client.id,
          ...args,
        }
      : skipToken,
    { ...opts, refetchOnMountOrArgChange: true, skip: !runPostSessionDrafts && !runInitialDrafts },
  ) as RTKQueryResultType<typeof useGetAIDraftQuery>
}
